import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { Skeleton } from '@mui/material';
import classNames from 'classnames';

import css from './CreativeView.css';
import { CreativeType } from '../../types';
import CreativeAsset from '../creativeAsset/CreativeAsset';
import { UNKNOWN_INDICATION } from '../../creativesFormattingUtils';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { trackMixpanelEvent } from '../../../utils/general';
import { TRACK_EVENT_PREFIX } from '../../consts';
import Tooltip from '../../../components/molecules/Tooltip';
import CreativeDimensionTags from './creativeDimensionTags/CreativeDimensionTags';

const ASSET_ASPECT_RATIO = 550 / 400;
const SIMILAR_CREATIVE_ASPECT_RATIO = 90 / 70;
const SIMILAR_CREATIVES_SKELETONS = [...Array(4)];
const SIMILAR_CREATIVES_KEY_PREFIX = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.SIMILAR_CREATIVES';

export const getDimensionsTranslationKey = key => `STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.${key}`;
export const formatValues = values => (values.length ? values.join(', ') : UNKNOWN_INDICATION);

const CreativeView = ({
    ref,
    creative,
    app,
    source,
    similarCreatives = [],
    isLoadingSimilarCreatives = false,
    similarCreativesError = null,
    onSimilarCreativeClick: onSimilarCreativeClickProp,
}) => {
    const [videoDuration, setVideoDuration] = useState(null);

    const { assetIdList, assetNameList, creativeIdList, creativeNameList } = creative.multiValuedDimensions;
    const isAssetName = !!assetNameList?.length;
    const isAssetId = !!assetIdList?.length;

    const handleVideoLoad = () => {
        if (ref?.current?.duration) setVideoDuration(ref.current.duration);
    };

    const onSimilarCreativeClick = (e, similarCreativeHash) => {
        e.preventDefault();
        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Similar Creative Clicked', {
            app: app.label,
            source: source.label,
            creativeType: creative.type,
        });
        onSimilarCreativeClickProp(similarCreativeHash);
    };

    return (
        <div className={css.creativeView}>
            <CreativeDimensionTags creative={creative} source={source} app={app} videoDuration={videoDuration} />
            <CreativeAsset
                key={creative.imageHash}
                ref={ref}
                type={creative.type}
                url={creative.url}
                embeddingUrl={creative.embeddingUrl}
                className={css.creativeAsset}
                controls
                aspectRatio={ASSET_ASPECT_RATIO}
                onLoad={handleVideoLoad}
                muted={false}
            />
            <div className={css.creativeRawData}>
                <Translate
                    id={getDimensionsTranslationKey(`${isAssetName ? 'ASSET' : 'CREATIVE'}_NAME`)}
                    data={{ value: formatValues(isAssetName ? assetNameList : creativeNameList || []) }}
                />
                <Translate
                    id={getDimensionsTranslationKey(`${isAssetId ? 'ASSET' : 'CREATIVE'}_ID`)}
                    data={{ value: formatValues(isAssetId ? assetIdList : creativeIdList || []) }}
                />
            </div>
            <div className={css.similarCreativesSection}>
                {!similarCreativesError && (
                    <Translate
                        id={`${SIMILAR_CREATIVES_KEY_PREFIX}.${isLoadingSimilarCreatives ? 'LOADING_TITLE' : 'TITLE'}`}
                        data={{ count: similarCreatives.length }}
                    />
                )}
                <div className={css.container}>
                    {isLoadingSimilarCreatives ? (
                        SIMILAR_CREATIVES_SKELETONS.map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rectangular"
                                className={css.similarCreativeItem}
                                animation="wave"
                            />
                        ))
                    ) : similarCreativesError ? (
                        <Translate id={`${SIMILAR_CREATIVES_KEY_PREFIX}.ERROR`} />
                    ) : (
                        similarCreatives.map(({ type, url, embeddingUrl, imageHash, multiValuedDimensions }) => {
                            const {
                                assetNameList: assetNames,
                                creativeNameList: creativeNames,
                            } = multiValuedDimensions;
                            const namesList = assetNames?.length ? assetNames : creativeNames || [];
                            const tooltipTitle = formatValues(namesList);
                            return (
                                <Tooltip key={url} title={tooltipTitle}>
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={e => onSimilarCreativeClick(e, imageHash)}
                                        className={css.clickableCreative}
                                    >
                                        <CreativeAsset
                                            type={type}
                                            url={url}
                                            aspectRatio={SIMILAR_CREATIVE_ASPECT_RATIO}
                                            embeddingUrl={embeddingUrl}
                                            className={classNames(css.similarCreativeItem, css.asset)}
                                        />
                                    </a>
                                </Tooltip>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

CreativeView.propTypes = {
    ref: PropTypes.shape({ current: Element }),
    creative: PropTypes.shape(CreativeType).isRequired,
    app: AutoCompleteOptionType.isRequired,
    source: AutoCompleteOptionType.isRequired,
    similarCreatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    isLoadingSimilarCreatives: PropTypes.bool,
    similarCreativesError: PropTypes.object,
    onSimilarCreativeClick: PropTypes.func.isRequired,
};

export default CreativeView;
